import { useState } from "react";
import type { ComponentProps } from "./index";
import {
	coerceFieldValue,
	defaultFieldValue,
	parseFields,
	shapeFormResult,
	unreadableAnsweredFields,
	type FieldSpec,
	type FieldValue,
} from "./formFields";
import { GONE_NOTICE, isGone, isGoneStatus, parseInteractiveResult, submitInteractiveResult } from "./interactive";
import { BasicControl, FieldRow } from "./FormField";

type SubmitState =
	| { phase: "idle" }
	| { phase: "submitting" }
	| { phase: "submitted" }
	| { phase: "failed"; status: number; message: string };

/**
 * Renders `{ fields: [{name, label, type, options?, value?}], prompt? }` as a form; result is an
 * object mapping each field's `name` to what the user entered.
 *
 * **Deliberate exception to this phase's "component arguments are references, not data" rule**
 * (see the top of index.tsx and Table.tsx's own comment). That rule exists to stop a model
 * *retyping* rows that code already computed — a match score or an apply URL drifting on its way
 * to the screen. A form's `fields` has no such source to drift from: it is short, authored fresh
 * for this one call, and there is no file behind it to reference instead — so it rides along as
 * data in the tool call's own arguments, same as any other tool.
 *
 * `result`'s presence is the only "already answered" signal — see the comment on Checklist.tsx
 * (identical reasoning) and `ComponentProps` in index.tsx.
 */
export function Form({ sessionId, callId, args, result }: ComponentProps): React.ReactNode {
	const prompt = typeof args.prompt === "string" ? args.prompt : undefined;
	const parsed = parseFields(args.fields);

	// Only what the user changed: `args` streams in after mount, so a field's draft is read from it
	// on every render rather than copied here once.
	const [state, setState] = useState<Record<string, FieldValue>>({});
	const [submit, setSubmit] = useState<SubmitState>({ phase: "idle" });

	if (!parsed.ok) {
		return <div className="component error">{parsed.error}</div>;
	}
	const fields = parsed.fields;

	const answered = result ? parseInteractiveResult(result) : undefined;
	// A field whose key is absent, or present with a value its own type can't actually use (a
	// select value outside its `options`, a non-boolean checkbox), would otherwise silently show
	// `coerceFieldValue`'s type-appropriate substitute (empty string / false / first option) as if
	// that were the real submitted value. Named explicitly below instead, same principle as
	// Checklist.tsx's `unreadable`/unresolved-id handling.
	const unreadableFields = answered?.kind === "value" ? unreadableAnsweredFields(fields, answered.value) : [];
	// Cancelled, timed out, or the server restarted. The filled-in fields stay on screen,
	// disabled, with one plain line instead of an error — see `isGone`.
	const gone = isGone(answered, submit.phase === "failed" ? submit.status : undefined);
	const locked =
		answered !== undefined ||
		submit.phase === "submitting" ||
		submit.phase === "submitted" ||
		(submit.phase === "failed" && isGoneStatus(submit.status));

	function setValue(name: string, value: FieldValue) {
		setState((prev) => ({ ...prev, [name]: value }));
	}

	async function onSubmit() {
		setSubmit({ phase: "submitting" });
		const outcome = await submitInteractiveResult(sessionId, callId, shapeFormResult(fields, state));
		if (outcome.ok) {
			setSubmit({ phase: "submitted" });
			return;
		}
		setSubmit({ phase: "failed", status: outcome.status, message: outcome.message });
	}

	return (
		<div className="component form-component">
			{prompt && <p className="component-prompt">{prompt}</p>}
			<div className="form-fields">
				{fields.map((f) => (
					<FieldRow key={f.name} label={f.label} hint={f.hint}>
						<BasicControl
							field={f}
							value={answered?.kind === "value" ? coerceFieldValue(f, answered.value[f.name]) : (state[f.name] ?? defaultFieldValue(f))}
							disabled={locked}
							onChange={(v) => setValue(f.name, v)}
						/>
					</FieldRow>
				))}
			</div>

			{gone && <p className="hint">{GONE_NOTICE.form}</p>}
			{answered?.kind === "unparseable" && <p className="error">已提交，无法读取提交内容</p>}
			{unreadableFields.length > 0 && (
				<p className="error">
					{unreadableFields.length} 个字段无法从提交结果读取，显示为默认值：
					{unreadableFields.map((f) => f.label).join("、")}。
				</p>
			)}
			{answered === undefined && submit.phase === "failed" && !gone && <p className="error">{submit.message}</p>}

			{/* No submit button once the call is gone: the notice above already says the filled-in
			    values will not land, and a disabled button under disabled fields only repeats it. */}
			{answered === undefined && !gone && (
				<button onClick={() => void onSubmit()} disabled={locked}>
					{submit.phase === "submitting" ? "提交中…" : submit.phase === "submitted" ? "已提交" : "提交"}
				</button>
			)}
		</div>
	);
}
